/** SUNTRONIX ONE — Customer Inventory Cockpit.
 *  Premium "Reuse before Buy" experience.  Clean tiles, no clutter.
 *  Every number drills to a filtered asset list (§36).
 *  All motion CSS-only.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowRight, Boxes, Building2, CheckCircle2, ChevronDown, Clock, Filter, LifeBuoy, Loader2, Recycle, Search, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isCustomerRole } from "@/constants/customerNav";
import OneBefore from "@/components/app/OneBefore";
import MobileFilterSheet from "@/components/app/MobileFilterSheet";

const TILE_ORDER = [
  { key: "total_owned",            label: "Total owned",        tone: "text" },
  { key: "with_employees",         label: "With employees",     tone: "text" },
  { key: "at_suntronix",           label: "At Suntronix",       tone: "text" },
  { key: "available_new",          label: "New — ready",        tone: "accent" },
  { key: "ready_to_deploy",        label: "Ready to deploy",    tone: "accent" },
  { key: "ready_for_redeployment", label: "Ready for reuse",    tone: "accent" },
  { key: "reserved",               label: "Reserved",           tone: "muted" },
  { key: "in_repair",              label: "In repair",          tone: "warn" },
  { key: "quarantined",            label: "Quarantined",        tone: "warn" },
  { key: "itad_pending",           label: "ITAD pending",       tone: "muted" },
];

const AVAIL_FILTERS = [
  { key: "",                          label: "All"          },
  { key: "available_new",             label: "New"          },
  { key: "ready_to_deploy",           label: "Ready to deploy" },
  { key: "ready_for_redeployment",    label: "Ready for reuse" },
  { key: "reserved",                  label: "Reserved"     },
  { key: "repair_required",           label: "Repair"       },
  { key: "quarantined",               label: "Quarantine"   },
];

export default function MyInventory() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [masters, setMasters] = useState([]);
  const [tenantId, setTenantId] = useState(user?.master_company_id || null);
  const [tiles, setTiles] = useState({});
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [avail, setAvail] = useState(params.get("availability") || "");

  const isSuntronix = !user?.master_company_id;

  useEffect(() => {
    if (!isSuntronix) return;
    api.get("/master-companies").then(({ data }) => {
      setMasters(data.items || []);
      if (!tenantId && data.items?.[0]) setTenantId(data.items[0].id);
    }).catch(() => {});
  }, [isSuntronix, tenantId]);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    const p = new URLSearchParams({ master_company_id: tenantId });
    Promise.all([
      api.get(`/customer-inventory/summary?${p.toString()}`),
      api.get(`/customer-inventory/search?${p.toString()}${avail ? `&availability=${avail}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
    ]).then(([summary, search]) => {
      setTiles(summary.data?.tiles || {});
      setRows(search.data?.items || []);
    }).finally(() => setLoading(false));
  }, [tenantId, avail, q]);

  const tenantName = useMemo(
    () => masters.find((m) => m.id === tenantId)?.name || (isSuntronix ? "Pick a tenant" : "Your organisation"),
    [masters, tenantId, isSuntronix]
  );

  const applyFilter = (key) => {
    setAvail(key);
    setParams(key ? { availability: key } : {});
  };

  return (
    <div className="max-w-6xl space-y-6" data-testid="my-inventory">
      {/* Header — the customer's story */}
      <div className="st-page-header">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">Technology · Inventory</div>
          <h1 className="st-page-title">Your inventory at Suntronix</h1>
          <p className="st-page-context">
            {tenantName} · Customer-owned technology tracked by ONE.  Every number drills to the actual assets.
          </p>
        </div>
        {isSuntronix && masters.length > 0 && (
          <select value={tenantId || ""} onChange={(e) => setTenantId(e.target.value)}
            data-testid="inventory-tenant-picker"
            className="h-9 px-3 rounded-md border border-[color:var(--st-border)] bg-white text-[12px]">
            {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
      </div>

      {/* Tiles — the What Do You Have answer */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="inventory-tiles">
        {TILE_ORDER.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              const map = { available_new: "available_new", ready_to_deploy: "ready_to_deploy",
                ready_for_redeployment: "ready_for_redeployment", reserved: "reserved",
                in_repair: "repair_required", quarantined: "quarantined", itad_pending: "itad_pending" };
              if (map[t.key]) applyFilter(map[t.key]);
            }}
            data-testid={`tile-${t.key}`}
            className="st-panel p-3 text-left hover:border-[color:var(--st-text)] transition-colors"
          >
            <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-muted)]">{t.label}</div>
            <div className={
              "mt-1 font-heading font-semibold text-[28px] tabular-nums " +
              (t.tone === "accent" ? "text-[color:var(--st-accent)]" :
               t.tone === "warn"   ? "text-[color:var(--st-warn)]"   :
               t.tone === "muted"  ? "text-[color:var(--st-text-muted)]" :
                                     "text-[color:var(--st-text)]")
            }>
              {loading ? "—" : (tiles[t.key] ?? 0)}
            </div>
          </button>
        ))}
      </section>

      {/* Actionable insights row — inspired by §12-14, §29 */}
      <section className="grid md:grid-cols-3 gap-3" data-testid="inventory-signals">
        <Signal
          testId="signal-reuse-ready"
          icon={Recycle}
          title={`${tiles.ready_for_redeployment ?? 0} devices ready to reuse`}
          body="Redeploy from your own warehouse before purchasing anything new."
          cta="Review candidates"
          onClick={() => applyFilter("ready_for_redeployment")}
        />
        <Signal
          testId="signal-repair"
          icon={AlertCircle}
          title={`${tiles.in_repair ?? 0} awaiting repair decision`}
          body="Repair, redeploy or retire — customer approval keeps these devices flowing."
          cta="Resolve blockers"
          onClick={() => applyFilter("repair_required")}
        />
        <Signal
          testId="signal-quarantine"
          icon={ShieldCheck}
          title={`${tiles.quarantined ?? 0} in quarantine`}
          body="Cannot be deployed until identity, security or damage is resolved."
          cta="Review holds"
          onClick={() => applyFilter("quarantined")}
        />
      </section>

      {/* ONE Before · demonstration for the buy action.  Any workflow can drop
          <OneBefore action="…"> at the moment of decision. */}
      {tenantId && (
        <OneBefore
          action="buy"
          masterCompanyId={tenantId}
          quantity={10}
          onContinue={() => { /* upstream workflow proceeds */ }}
        />
      )}

      {/* Filter strip + inventory table */}
      <section>
        {/* Below lg: search + filter sheet trigger */}
        <div className="flex lg:hidden items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[color:var(--st-text-hint)]" />
            <input
              placeholder="Search serial, tag, model…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="inventory-search-mobile"
              className="h-10 pl-8 pr-3 w-full rounded-md border border-[color:var(--st-border)] bg-white text-[13px] focus:outline-none focus:border-[color:var(--st-text)]"
            />
          </div>
          <MobileFilterSheet
            testId="inventory-mobile-filters"
            triggerLabel="Filters"
            activeCount={avail ? 1 : 0}
            onApply={() => { /* filters apply live via applyFilter */ }}
            onClear={() => applyFilter("")}
          >
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 mb-2">Availability</div>
              <div className="flex flex-wrap gap-2">
                {AVAIL_FILTERS.map((f) => (
                  <button key={f.key || "all"}
                    onClick={() => applyFilter(f.key)}
                    data-testid={`mobile-filter-${f.key || "all"}`}
                    className={
                      "h-10 px-3 rounded-md text-[12px] uppercase tracking-[0.10em] min-h-[40px] " +
                      (avail === f.key
                        ? "bg-slate-900 text-white"
                        : "border border-slate-200 text-slate-600 hover:text-slate-900")
                    }>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </MobileFilterSheet>
        </div>

        {/* Desktop (lg+): inline filter strip */}
        <div className="hidden lg:flex items-center gap-2 mb-3 flex-wrap" data-testid="inventory-filter-strip">
          <Filter className="h-3.5 w-3.5 text-[color:var(--st-text-muted)]" />
          {AVAIL_FILTERS.map((f) => (
            <button key={f.key || "all"}
              onClick={() => applyFilter(f.key)}
              data-testid={`filter-${f.key || "all"}`}
              className={
                "h-7 px-2.5 rounded-md text-[11px] uppercase tracking-[0.12em] " +
                (avail === f.key
                  ? "bg-[color:var(--st-text)] text-white"
                  : "border border-[color:var(--st-border)] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]")
              }>
              {f.label}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[color:var(--st-text-hint)]" />
            <input
              placeholder="Search serial, tag, model…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="inventory-search"
              className="h-8 pl-8 pr-3 w-64 rounded-md border border-[color:var(--st-border)] bg-white text-[12px] focus:outline-none focus:border-[color:var(--st-text)]"
            />
          </div>
        </div>

        <div className="st-panel overflow-hidden" data-testid="inventory-table">
          {loading ? (
            <div className="p-8 text-center text-[12px] text-[color:var(--st-text-muted)]"><Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center">
              <Boxes className="mx-auto h-6 w-6 text-[color:var(--st-text-hint)]" />
              <div className="mt-2 text-[13px] text-[color:var(--st-text-muted)]">No assets match this view.</div>
              <div className="text-[11px] text-[color:var(--st-text-subtle)] mt-1">Try a different filter or search.</div>
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)] border-b border-[color:var(--st-border)]">
                  <th className="px-3 py-2" data-priority="p1">Asset</th>
                  <th className="px-3 py-2 hidden md:table-cell" data-priority="p2">Serial</th>
                  <th className="px-3 py-2" data-priority="p1">Availability</th>
                  <th className="px-3 py-2 hidden lg:table-cell" data-priority="p3">Grade</th>
                  <th className="px-3 py-2 hidden lg:table-cell" data-priority="p3">Cycles</th>
                  <th className="px-3 py-2 hidden md:table-cell" data-priority="p2">Location</th>
                  <th className="px-3 py-2 text-right" data-priority="p1">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} className="border-t border-[color:var(--st-border)] hover:bg-[color:var(--st-surface-alt)]" data-testid={`inventory-row-${a.id}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-[color:var(--st-text)]">{a.model || a.name || "—"}</div>
                      <div className="text-[11px] text-[color:var(--st-text-muted)]">{a.brand || a.category || ""}</div>
                      <div className="md:hidden text-[11px] text-[color:var(--st-text-muted)] font-mono mt-0.5">{a.serial_number || a.asset_tag || ""}</div>
                    </td>
                    <td className="px-3 py-2.5 tabular-nums text-[color:var(--st-text-muted)] hidden md:table-cell">{a.serial_number || a.asset_tag || "—"}</td>
                    <td className="px-3 py-2.5"><AvailBadge value={a.availability} /></td>
                    <td className="px-3 py-2.5 text-[color:var(--st-text-muted)] hidden lg:table-cell">{a.condition_grade || "—"}</td>
                    <td className="px-3 py-2.5 tabular-nums text-[color:var(--st-text-muted)] hidden lg:table-cell">{(a.deployment_cycles || []).length}</td>
                    <td className="px-3 py-2.5 text-[color:var(--st-text-muted)] hidden md:table-cell">{a.location || a.warehouse || "—"}</td>
                    <td className="px-3 py-2.5 text-right">
                      <Link to={`/inventory/passport/${a.id}`} data-testid={`passport-${a.id}`}
                        className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--st-accent)] hover:underline">
                        Passport →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function Signal({ icon: Icon, title, body, cta, onClick, testId }) {
  return (
    <div className="st-panel p-4" data-testid={testId}>
      <Icon className="h-4 w-4 text-[color:var(--st-accent)]" strokeWidth={1.6} />
      <div className="text-[14px] font-semibold text-[color:var(--st-text)] mt-2">{title}</div>
      <div className="text-[12px] text-[color:var(--st-text-muted)] mt-1">{body}</div>
      <button onClick={onClick} className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-[color:var(--st-accent)] hover:underline">
        {cta} <ArrowRight className="h-3 w-3" />
      </button>
    </div>
  );
}

const AVAIL_STYLE = {
  available_new:          "bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)]",
  ready_to_deploy:        "bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)]",
  ready_for_redeployment: "bg-emerald-100 text-emerald-800",
  reserved:               "bg-slate-100 text-slate-700",
  in_use:                 "bg-slate-800 text-white",
  repair_required:        "bg-amber-100 text-amber-800",
  quarantined:            "bg-rose-100 text-rose-800",
  itad_pending:           "bg-slate-100 text-slate-500",
};
function AvailBadge({ value }) {
  if (!value) return <span className="text-[color:var(--st-text-hint)]">—</span>;
  const cls = AVAIL_STYLE[value] || "bg-slate-100 text-slate-600";
  return <span className={"inline-block text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded font-medium " + cls}>{value.replace(/_/g, " ")}</span>;
}
